/**
 * Profile Page Component
 * User profile, goals from API, and settings
 */

import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ModeToggle } from '@/components/ui/mode-toggle';
import { User, Target, Settings, LogOut, Edit, History, MessageSquare } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { goalsService } from '@/services/goals.service';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { EditGoalDrawer } from '@/components/goals/EditGoalDrawer';

function formatDate(s: string): string {
  try {
    const d = new Date(s + 'T00:00:00');
    return d.toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return s;
  }
}

function getInitials(email: string | undefined): string {
  if (!email) return 'ผู้ใช้';
  const part = email.split('@')[0] ?? '';
  if (part.length >= 2) return part.slice(0, 2).toUpperCase();
  return part.slice(0, 1).toUpperCase();
}

export function ProfilePage() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  const { data: goals = [], isLoading: goalsLoading } = useQuery({
    queryKey: ['goals'],
    queryFn: () => goalsService.listGoals(),
  });

  // Get current goal for editing
  const { data: currentGoal } = useQuery({
    queryKey: ['goals', 'current'],
    queryFn: () => goalsService.getCurrentGoal(),
  });

  const handleLogout = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="min-h-screen p-6 space-y-6">
      {/* Header */}
      <header className="space-y-2">
        <h1 className="text-3xl font-bold text-violet-900 dark:text-violet-100">
          โปรไฟล์
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          จัดการข้อมูลและการตั้งค่าของคุณ
        </p>
      </header>

      {/* User Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            ข้อมูลผู้ใช้
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <Avatar className="h-16 w-16">
              <AvatarImage
                src={
                  (user?.user_metadata as { avatar_url?: string; picture?: string } | undefined)
                    ?.avatar_url ??
                  (user?.user_metadata as { avatar_url?: string; picture?: string } | undefined)
                    ?.picture
                }
                alt={user?.email ?? 'โปรไฟล์'}
              />
              <AvatarFallback className="bg-violet-100 text-violet-700 text-lg dark:bg-violet-900 dark:text-violet-300">
                {getInitials(user?.email)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="font-semibold text-slate-900 dark:text-slate-100">
                {user?.email || 'ผู้ใช้'}
              </p>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                สมาชิกตั้งแต่ {new Date().getFullYear()}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Feature Sections */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* Goals */}
        <Card
          className={cn(
            'transition-all duration-200',
            'hover:shadow-lg'
          )}
        >
          <CardHeader>
            <div className="flex items-start justify-between">
              <div 
                className="flex-1 cursor-pointer"
                onClick={() => navigate('/dashboard')}
              >
                <CardTitle className="flex items-center gap-2 text-lg mb-1">
                  <Target className="w-5 h-5 text-violet-600" />
                  เป้าหมาย
                </CardTitle>
                <CardDescription>
                  ตั้งและติดตามเป้าหมายการอ่าน
                </CardDescription>
              </div>
              {currentGoal && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditDrawerOpen(true);
                  }}
                  className="shrink-0"
                >
                  <Edit className="h-4 w-4" />
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {goalsLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : goals.length === 0 ? (
              <>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  ยังไม่มีเป้าหมาย — ไปตั้งเป้าหมายที่แดชบอร์ด
                </p>
                <Link
                  to="/goal-history"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
                >
                  <History className="w-4 h-4" />
                  ดูประวัติเป้าหมาย
                </Link>
              </>
            ) : (
              <>
                <ul className="space-y-2 text-sm">
                  {goals.slice(0, 3).map((goal) => (
                    <li
                      key={goal.id}
                      className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50/50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900/50"
                    >
                      <span className="font-medium text-slate-900 dark:text-slate-100">
                        {goal.khatm_count.toLocaleString('th-TH')} Khatm
                      </span>
                      <span className="text-slate-500 dark:text-slate-400">
                        {formatDate(goal.start_date)} – {formatDate(goal.end_date)}
                      </span>
                      <span
                        className={cn(
                          'rounded-full px-2 py-0.5 text-xs font-medium',
                          goal.status === 'active'
                            ? 'bg-violet-100 text-violet-700 dark:bg-violet-900/50 dark:text-violet-300'
                            : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-400'
                        )}
                      >
                        {goal.status === 'active' ? 'กำลังทำ' : goal.status === 'completed' ? 'เสร็จ' : 'ถูกแทนที่'}
                      </span>
                    </li>
                  ))}
                  {goals.length > 3 && (
                    <p className="text-xs text-slate-500 dark:text-slate-400">
                      และอีก {(goals.length - 3).toLocaleString('th-TH')} เป้าหมาย
                    </p>
                  )}
                </ul>
                <Link
                  to="/goal-history"
                  className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-violet-600 dark:text-violet-400 hover:underline"
                >
                  <History className="w-4 h-4" />
                  ดูประวัติเป้าหมายทั้งหมด
                </Link>
              </>
            )}
          </CardContent>
        </Card>

        {/* Feedback */}
        <Card
          className={cn(
            'transition-all duration-200',
            'hover:shadow-lg'
          )}
        >
          <CardHeader>
            <Link
              to="/feedback"
              className="flex-1 cursor-pointer block"
            >
              <CardTitle className="flex items-center gap-2 text-lg mb-1">
                <MessageSquare className="w-5 h-5 text-violet-600" />
                แบบประเมินความพึงพอใจ
              </CardTitle>
              <CardDescription>
                ช่วยเราพัฒนาแอปด้วยการส่งความเห็น
              </CardDescription>
            </Link>
          </CardHeader>
          <CardContent>
            <Link to="/feedback">
              <Button variant="outline" className="w-full">
                feedback เพื่อพัฒนาแอปไปด้วยกัน
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>

      {/* Settings Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            การตั้งค่า
          </CardTitle>
          <CardDescription>
            ปรับแต่งประสบการณ์การใช้งาน
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  ภาษา
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  เลือกภาษาที่ใช้แสดงผล
                </p>
              </div>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                ไทย
              </span>
            </div>

            <div className="flex items-center justify-between py-3 border-b border-slate-200 dark:border-slate-800">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  การแจ้งเตือน
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  แจ้งเตือนเมื่อถึงเวลาอ่าน
                </p>
              </div>
              <span className="text-sm text-slate-600 dark:text-slate-400">
                กำลังพัฒนา
              </span>
            </div>

            <div className="flex items-center justify-between py-3">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">
                  ธีม
                </p>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  เปลี่ยนธีมสีของแอป (มืด/สว่าง)
                </p>
              </div>
              <ModeToggle />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Logout */}
      <Button
        type="button"
        variant="destructive"
        className="w-full gap-2"
        onClick={handleLogout}
      >
        <LogOut className="w-4 h-4" />
        ออกจากระบบ
      </Button>

      {/* Edit Goal Drawer */}
      <EditGoalDrawer
        open={editDrawerOpen}
        onClose={() => setEditDrawerOpen(false)}
        goal={currentGoal || null}
      />
    </div>
  );
}

export default ProfilePage;
